import type { SyncFilter } from '@awork/core/state/signal-store/types'
import {
  type PersistedStoreDefinition,
  getConfiguredPersistedStoreDefinition,
  getAppPersistedStoreDefinition,
  PERSIST_STATE_LEGACY_STORE_NAME,
  PERSIST_STATE_META_ROW_KEY,
  PERSIST_STATE_DB_VERSION,
  PERSIST_STATE_EMPTY_META_ROW
} from './persistStateSchema'

/**
 * Persisted metadata for entity signal stores.
 */
export interface PersistedEntityMeta {
  active: string | null
  loading: boolean
  error: Error | null
  lastSync: Date | null
  loadedOn?: Date | null
  syncFilters: SyncFilter[]
}

/**
 * Legacy persisted entity-store payload from the version-4 single-store schema.
 */
export interface PersistedEntityState<Entity> extends PersistedEntityMeta {
  entities: Entity[]
  ids: string[]
}

/**
 * Persisted metadata for plain signal stores.
 */
export interface PersistedPlainStateMeta {
  loading: boolean
  error: Error | null
  loadedOn?: Date | null
}

interface ExportedEntityStore {
  meta?: PersistedEntityMeta
  entities: Record<string, unknown>
}

/**
 * Normalized in-memory representation used during export/import and schema rebuilds.
 */
export interface ExportedPersistedData {
  plainStores: Map<string, unknown>
  entityStores: Map<string, ExportedEntityStore>
}

const LEGACY_V4_RETAINED_STORE_NAMES = new Set(['account', 'app', 'workspace'])

/**
 * Determines whether the current IndexedDB schema needs to be rebuilt.
 * @param {IDBDatabase} db
 * @param {string[]} configuredStoreNames
 * @returns {boolean}
 */
export function requiresPersistStateDatabaseRebuild(db: IDBDatabase, configuredStoreNames: string[]): boolean {
  if (db.version !== PERSIST_STATE_DB_VERSION) {
    return true
  }

  const objectStoreNames = Array.from(db.objectStoreNames)

  return (
    objectStoreNames.length !== configuredStoreNames.length ||
    configuredStoreNames.some(storeName => !db.objectStoreNames.contains(storeName)) ||
    objectStoreNames.some(storeName => !configuredStoreNames.includes(storeName))
  )
}

/**
 * Creates the two key ranges that hold every row except the metadata row, in key order.
 * @returns {IDBKeyRange[]}
 */
export function createRowRangesWithoutMeta(): IDBKeyRange[] {
  return [
    IDBKeyRange.upperBound(PERSIST_STATE_META_ROW_KEY, true),
    IDBKeyRange.lowerBound(PERSIST_STATE_META_ROW_KEY, true)
  ]
}

/**
 * Reads all rows from a single IndexedDB object store.
 * Uses `getAll` and `getAllKeys`: a cursor needs one task per row, which is slow while the app starts.
 * @param {IDBDatabase} db
 * @param {string} objectStoreName
 * @returns {Promise<Record<string, unknown>>}
 */
export function readObjectStoreRows(db: IDBDatabase, objectStoreName: string): Promise<Record<string, unknown>> {
  return new Promise<Record<string, unknown>>((resolve, reject) => {
    const transaction = db.transaction(objectStoreName, 'readonly')
    const objectStore = transaction.objectStore(objectStoreName)
    const keysRequest = objectStore.getAllKeys()
    const valuesRequest = objectStore.getAll()

    transaction.oncomplete = () => {
      const values = valuesRequest.result

      resolve(Object.fromEntries(keysRequest.result.map((key, index) => [String(key), values[index]])))
    }
    // A failed request aborts the transaction. `transaction.error` is set by then, but not yet in `onerror`.
    transaction.onabort = () => reject(transaction.error)
  })
}

/**
 * Exports persisted data from either the legacy single-store schema or the current dedicated-store schema.
 * @param {IDBDatabase} db
 * @param {Iterable<PersistedStoreDefinition>} [additionalStoreDefinitions=[]] Optional test-only store definitions.
 * @returns {Promise<ExportedPersistedData>}
 */
export async function exportPersistedData(
  db: IDBDatabase,
  additionalStoreDefinitions: Iterable<PersistedStoreDefinition> = []
): Promise<ExportedPersistedData> {
  const persistedData: ExportedPersistedData = { plainStores: new Map(), entityStores: new Map() }
  const additionalStoreDefinitionsList = [...additionalStoreDefinitions]
  const objectStoreNames = Array.from(db.objectStoreNames)

  if (objectStoreNames.includes(PERSIST_STATE_LEGACY_STORE_NAME)) {
    const legacyRows = await readObjectStoreRows(db, PERSIST_STATE_LEGACY_STORE_NAME)

    captureLegacyRows(legacyRows, persistedData)
  }

  for (const objectStoreName of objectStoreNames.filter(name => name !== PERSIST_STATE_LEGACY_STORE_NAME)) {
    const storeRows = await readObjectStoreRows(db, objectStoreName)

    captureDedicatedStoreRows(objectStoreName, storeRows, persistedData, additionalStoreDefinitionsList)
  }

  return persistedData
}

/**
 * Imports canonical persisted data into the dedicated-store schema.
 * @param {IDBDatabase} db
 * @param {ExportedPersistedData} persistedData
 * @param {string[]} configuredStoreNames
 * @returns {Promise<void>}
 */
export function importPersistedData(
  db: IDBDatabase,
  persistedData: ExportedPersistedData,
  configuredStoreNames: string[]
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const transactionStoreNames = configuredStoreNames.filter(
      storeName => persistedData.plainStores.has(storeName) || persistedData.entityStores.has(storeName)
    )

    if (!transactionStoreNames.length) {
      resolve()

      return
    }

    const transaction = db.transaction(transactionStoreNames, 'readwrite')

    transactionStoreNames.forEach(storeName => {
      const objectStore = transaction.objectStore(storeName)

      objectStore.clear()
      objectStore.put(PERSIST_STATE_EMPTY_META_ROW, PERSIST_STATE_META_ROW_KEY)
    })

    persistedData.plainStores.forEach((persistedState, storeName) => {
      if (!configuredStoreNames.includes(storeName)) {
        return
      }

      const objectStore = transaction.objectStore(storeName)
      const rows = packPlainStateRows(persistedState)

      Object.entries(rows).forEach(([rowKey, rowValue]) => {
        objectStore.put(rowValue, rowKey)
      })
    })

    persistedData.entityStores.forEach(({ meta, entities }, storeName) => {
      if (!configuredStoreNames.includes(storeName)) {
        return
      }

      const objectStore = transaction.objectStore(storeName)

      if (meta) {
        objectStore.put(meta, PERSIST_STATE_META_ROW_KEY)
      }

      Object.entries(entities).forEach(([entityId, entity]) => {
        objectStore.put(entity, entityId)
      })
    })

    transaction.oncomplete = () => resolve()
    transaction.onabort = transaction.onerror = () => reject(transaction.error)
  })
}

/**
 * Flattens a plain persisted state so its properties become individual IndexedDB rows.
 * @param {unknown} value
 * @returns {Record<string, unknown>}
 */
export function packPlainStateRows(value: unknown): Record<string, unknown> {
  if (!isPlainPersistedState(value)) {
    return {}
  }

  return {
    ...(value.state || {}),
    [PERSIST_STATE_META_ROW_KEY]: {
      loading: value.loading,
      error: value.error,
      loadedOn: value.loadedOn || null
    }
  }
}

/**
 * Reconstructs a plain persisted state from dedicated object-store rows.
 * @param {Record<string, unknown>} rows
 * @returns {T}
 */
export function unpackPlainStateRows<T>(rows: Record<string, unknown>): T {
  const meta = rows[PERSIST_STATE_META_ROW_KEY] as Record<string, unknown> | undefined
  const state = { ...rows }

  delete state[PERSIST_STATE_META_ROW_KEY]

  const { loading = true, error = null, loadedOn = null } = meta || {}

  return {
    state,
    loading,
    error,
    loadedOn
  } as T
}

/**
 * Determines whether a metadata row is the empty initialization marker.
 * @param {unknown} value
 * @returns {boolean}
 */
export function isEmptyPersistedMetaRow(value: unknown): boolean {
  return !!value && typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0
}

/**
 * Copies only the retained legacy `awState` rows into the canonical export format.
 * Most version-4 rows are intentionally dropped because they can be re-fetched.
 * @param {Record<string, unknown>} rows
 * @param {ExportedPersistedData} persistedData
 */
function captureLegacyRows(rows: Record<string, unknown>, persistedData: ExportedPersistedData): void {
  Object.entries(rows).forEach(([key, value]) => {
    if (!LEGACY_V4_RETAINED_STORE_NAMES.has(key)) {
      return
    }

    const configuredStore = getAppPersistedStoreDefinition(key)

    if (configuredStore?.type === 'entity' && isLegacyPersistedEntityState(value)) {
      persistedData.entityStores.set(key, {
        meta: {
          active: value.active,
          loading: value.loading,
          error: value.error,
          lastSync: value.lastSync,
          loadedOn: value.loadedOn,
          syncFilters: value.syncFilters
        },
        entities: Object.fromEntries(value.entities.map(entity => [String(entity.id), entity]))
      })

      return
    }

    persistedData.plainStores.set(key, value)
  })
}

/**
 * Reads the raw rows of one dedicated object store and adds them to the normalized export data.
 * Empty stores are skipped and only configured stores are exported.
 * @param {string} storeName
 * @param {Record<string, unknown>} rows
 * @param {ExportedPersistedData} persistedData
 * @param {Iterable<PersistedStoreDefinition>} additionalStoreDefinitions
 */
function captureDedicatedStoreRows(
  storeName: string,
  rows: Record<string, unknown>,
  persistedData: ExportedPersistedData,
  additionalStoreDefinitions: Iterable<PersistedStoreDefinition>
): void {
  const configuredStore = getConfiguredPersistedStoreDefinition(storeName, additionalStoreDefinitions)

  if (!Object.keys(rows).length) {
    return
  }

  if (Object.keys(rows).length === 1 && isEmptyPersistedMetaRow(rows[PERSIST_STATE_META_ROW_KEY])) {
    return
  }

  if (configuredStore?.type === 'plain') {
    persistedData.plainStores.set(storeName, unpackPlainStateRows(rows))

    return
  }

  if (configuredStore?.type === 'entity') {
    captureDedicatedEntityStoreRows(storeName, rows, persistedData)

    return
  }
}

/**
 * Copies dedicated entity-store rows into the canonical export format.
 * @param {string} storeName
 * @param {Record<string, unknown>} rows
 * @param {ExportedPersistedData} persistedData
 */
function captureDedicatedEntityStoreRows(
  storeName: string,
  rows: Record<string, unknown>,
  persistedData: ExportedPersistedData
): void {
  const entityStore = getOrCreateExportedEntityStore(storeName, persistedData)

  if (PERSIST_STATE_META_ROW_KEY in rows && !isEmptyPersistedMetaRow(rows[PERSIST_STATE_META_ROW_KEY])) {
    entityStore.meta = rows[PERSIST_STATE_META_ROW_KEY] as PersistedEntityMeta
  }

  Object.entries(rows).forEach(([rowKey, rowValue]) => {
    if (rowKey === PERSIST_STATE_META_ROW_KEY) {
      return
    }

    entityStore.entities[rowKey] = rowValue
  })
}

/**
 * Gets or creates an exported entity-store bucket.
 * @param {string} storeName
 * @param {ExportedPersistedData} persistedData
 * @returns {ExportedEntityStore}
 */
function getOrCreateExportedEntityStore(storeName: string, persistedData: ExportedPersistedData): ExportedEntityStore {
  const existingStore = persistedData.entityStores.get(storeName)

  if (existingStore) {
    return existingStore
  }

  const entityStore: ExportedEntityStore = { entities: {} }

  persistedData.entityStores.set(storeName, entityStore)

  return entityStore
}

/**
 * Determines whether a value matches the persisted plain-store runtime shape.
 * @param {unknown} value
 * @returns {value is { state: Record<string, unknown>; loading: boolean; error: unknown; loadedOn?: unknown; }}
 */
function isPlainPersistedState(
  value: unknown
): value is { state: Record<string, unknown>; loading: boolean; error: unknown; loadedOn?: unknown } {
  return !!value && typeof value === 'object' && 'state' in value && 'loading' in value && 'error' in value
}

/**
 * Determines whether a legacy version-4 row matches the persisted entity-store shape.
 * @param {unknown} value
 * @returns {value is PersistedEntityState<{ id: string; }>}
 */
function isLegacyPersistedEntityState(value: unknown): value is PersistedEntityState<{ id: string }> {
  return (
    !!value &&
    typeof value === 'object' &&
    'entities' in value &&
    Array.isArray((value as PersistedEntityState<{ id: string }>).entities) &&
    'active' in value &&
    'lastSync' in value &&
    Array.isArray((value as PersistedEntityState<{ id: string }>).syncFilters)
  )
}
